import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  type ButtonProps,
  type DialogProps,
  styled,
} from '@mui/material';
import { Dialog, DialogActions, DialogContent } from '@side/pantry';
import type { DialogTitleProps } from '@side/pantry/esm/Dialog/DialogTitle';
import type { ReactNode } from 'react';
import {
  type DefaultValues,
  FormProvider,
  type SubmitHandler,
  useForm,
} from 'react-hook-form';
import type { AnyObject, Asserts, InferType, ObjectSchema } from 'yup';

const StyledForm = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'stretch',
  maxHeight: 'none',
  maxWidth: '100%',
  width: '100%',
  height: '100%',
});

export type FormDialogModalProps<
  T extends ObjectSchema<AnyObject, AnyObject, AnyObject, ''>,
> = {
  title: string;
  headline?: string | ReactNode;
  controls?: ReactNode;
  onSubmit: SubmitHandler<Asserts<T>>;
  onClose: NonNullable<DialogTitleProps['onClose']>;
  formSchema: T;
  formProps?: { 'aria-label'?: string };
  cancelButtonProps?: Omit<ButtonProps, 'onClick'> & {
    label?: string;
  };
  saveButtonProps?: Omit<ButtonProps, 'onClick' | 'disabled'> & {
    label?: string;
    enableOnDirty?: boolean;
    disabled?: boolean;
  };
  secondaryButtonProps?: Omit<ButtonProps, 'onClick'> & {
    label?: string;
    onClick?: () => void;
  };
  secondaryCancelButtonProps?: Omit<ButtonProps, 'onClick'> & {
    label?: string;
    onClick?: () => void;
  };
  showSecondaryButton?: boolean;
  defaultValues?: DefaultValues<InferType<T>>;
} & Omit<DialogProps, 'onSubmit' | 'onClose'>;

export function FormDialogModal<
  T extends ObjectSchema<AnyObject, AnyObject, AnyObject, ''>,
>(props: FormDialogModalProps<T>) {
  const {
    open,
    children,
    title,
    headline,
    cancelButtonProps,
    saveButtonProps,
    secondaryButtonProps,
    secondaryCancelButtonProps,
    showSecondaryButton,
    onSubmit,
    formSchema,
    defaultValues,
    formProps,
    onClose,
    ...rest
  } = props;

  const {
    enableOnDirty,
    disabled: externalDisabled,
    ...saveButtonComponentProps
  } = saveButtonProps || {};
  const { ...cancelButtonComponentProps } = cancelButtonProps || {};
  const { ...secondaryCancelButtonComponentProps } =
    secondaryCancelButtonProps || {};
  const { ...secondaryButtonComponent } = secondaryButtonProps || {};

  const formMethods = useForm<Asserts<T>>({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(formSchema),
    defaultValues,
  });
  const { isSubmitting, isDirty, isValid } = formMethods.formState;

  const handleSubmit: SubmitHandler<Asserts<T>> = async (data, e) => {
    try {
      await onSubmit(data, e);
      return formMethods.reset();
    } catch (_error) {
      // Do not close or reset the modal if there is an error
      return undefined;
    }
  };

  const handleSecondaryButton = () => {
    if (secondaryButtonComponent?.onClick) {
      secondaryButtonComponent?.onClick();
    }
    return onClose();
  };

  const handleSecondaryCancel = () => {
    if (secondaryButtonComponent?.onClick) {
      secondaryButtonComponent?.onClick();
    }
    return onClose();
  };

  const handleCancel = () => {
    formMethods.reset();
    return onClose();
  };

  const isSaveButtonDisabled =
    externalDisabled !== undefined
      ? externalDisabled
      : (enableOnDirty ? false : !isDirty) || !isValid;

  return (
    <Dialog open={open} onClose={onClose} {...rest} maxWidth="lg">
      <FormProvider {...formMethods}>
        <StyledForm
          onSubmit={formMethods.handleSubmit(handleSubmit)}
          {...formProps}
        >
          <DialogContent title={title} onClose={onClose}>
            {headline}
            {children}
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                md: 'row',
              },
              alignItems: {
                xs: 'stretch',
                md: 'end',
              },
              gap: 2,
              '&>:not(:first-of-type)': { marginLeft: 0 },
            }}
          >
            {showSecondaryButton ? (
              <>
                <Button
                  variant="outlined"
                  size="medium"
                  type="reset"
                  onClick={handleSecondaryCancel}
                  {...secondaryCancelButtonComponentProps}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={handleSecondaryButton}
                  disabled={isSaveButtonDisabled}
                  {...secondaryButtonComponent}
                >
                  {secondaryButtonProps?.label || 'Done'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  size="medium"
                  type="reset"
                  onClick={handleCancel}
                  {...cancelButtonComponentProps}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="medium"
                  type="submit"
                  {...saveButtonComponentProps}
                  disabled={(enableOnDirty ? false : !isDirty) || !isValid}
                  loading={isSubmitting}
                >
                  {saveButtonProps?.label || 'Save'}
                </Button>
              </>
            )}
          </DialogActions>
        </StyledForm>
      </FormProvider>
    </Dialog>
  );
}
